import { useEffect } from 'react';
import { getSearchParam } from 'helpers/getSearchParam';

import { actions, isEmptyCollection, useCasinoState, useCasinoStateDispatcher } from '@atlas/core';

import { GROUPS } from 'components/Casino/constants';
import history from 'components/Layout/history';

const casinoFilterTypeMap = {
  gameTypeNames: 'gameTypeNames',
  providerNames: 'providerNames',
  category: 'category',
  name: 'name',
};

const updateHistory = (pathname, searchParams) => {
  const filterEntry = [];

  // eslint-disable-next-line no-restricted-syntax,no-unused-vars
  for (const pair of searchParams.entries()) {
    filterEntry.push(`${pair[0]}=${pair[1]}`);
  }

  const search = filterEntry.join('&');
  const nextUrl = search ? `${pathname}?${search}` : pathname;

  if (nextUrl === `${history.location.pathname}${history.location.search}`) return;

  history.push(nextUrl);
};

const getFilter = filterType => {
  const { search } = history.location;
  const query = new URLSearchParams(search);

  return query.get(filterType);
};

const toggleCasinoFilterListItem = (filterType, gameTypeName) => {
  const { pathname, search } = history.location;
  const query = new URLSearchParams(search);
  const previousGameTypesStr = query.get(filterType);
  const previousGameTypes = previousGameTypesStr ? previousGameTypesStr.split(',') : [];
  let updatedGameTypes;

  if (previousGameTypes.includes(gameTypeName)) {
    updatedGameTypes = previousGameTypes.filter(type => type !== gameTypeName);
  } else {
    updatedGameTypes = [...previousGameTypes, gameTypeName];
  }

  if (isEmptyCollection(updatedGameTypes)) {
    query.delete(filterType);
  } else {
    query.set(filterType, encodeURIComponent(updatedGameTypes.join(',')));
  }

  updateHistory(pathname, query);
};

const updateCasinoFilter = (filterName, filterValue) => {
  const { pathname, search } = history.location;
  const query = new URLSearchParams(search);
  const slugFilterValue = encodeURIComponent(filterValue);

  query.set(filterName, slugFilterValue);

  updateHistory(pathname, query);
};

const deleteCasinoFilter = filterType => {
  const { pathname, search } = history.location;
  const query = new URLSearchParams(search);

  query.delete(filterType);

  updateHistory(pathname, query);
};

const generateFormattedFilter = (filterType, options, parameter = 'name') => {
  const filterStr = getFilter(filterType);
  const filterNames = filterStr ? filterStr.split(',') : [];

  return filterNames.length
    ? options.reduce((types, item) => {
        if (filterNames.includes(item.name)) {
          return [...types, item[parameter]];
        }
        return types;
      }, [])
    : [];
};

const useCasinoFilter = isLivePage => {
  const { categories, categoriesResolved, filter, gameTypes, providers } = useCasinoState();
  const dispatch = useCasinoStateDispatcher();

  const searchProviders = getSearchParam(window.location.search, 'providerNames');
  const searchString = getSearchParam(window.location.search, 'name');
  const searchCategory = getSearchParam(window.location.search, 'category');

  const updateFilter = filter => {
    dispatch({
      type: actions.CASINO_STATE_UPDATE,
      payload: {
        filter,
      },
    });
  };

  useEffect(() => {
    const newFilter = { ...filter };
    newFilter.providerNames = generateFormattedFilter(
      casinoFilterTypeMap.providerNames,
      providers,
      'name',
    );
    updateFilter(newFilter);
  }, []);

  useEffect(() => {
    if (categoriesResolved) {
      const newFilter = { ...filter };

      newFilter.group = isLivePage ? GROUPS.LIVE : GROUPS.CASINO;
      newFilter.gameMode = 'DESKTOP';

      newFilter.gameTypeNames = generateFormattedFilter(
        casinoFilterTypeMap.gameTypeNames,
        gameTypes,
        'name',
      );
      newFilter.providerNames = generateFormattedFilter(
        casinoFilterTypeMap.providerNames,
        providers,
        'name',
      );
      newFilter.categories = generateFormattedFilter(
        casinoFilterTypeMap.category,
        categories,
        'id',
      );
      newFilter.name = getFilter(casinoFilterTypeMap.name) || '';

      updateFilter(newFilter);
    }
  }, [categoriesResolved, searchProviders, searchString, searchCategory]);
};

export {
  casinoFilterTypeMap,
  deleteCasinoFilter,
  getFilter,
  toggleCasinoFilterListItem,
  updateCasinoFilter,
};
export default useCasinoFilter;
